class DrawerMenu extends HTMLElement {
  static getMenuData() {
    // @ts-ignore
    return window.menu_json;
  }

  static get prefix() {
    const theme = window.Theme || (typeof Theme !== 'undefined' ? Theme : undefined);
    return theme?.routes?.root_url_prefix || '/';
  }

  static getUrl(url) {
    const theme = window.Theme || (typeof Theme !== 'undefined' ? Theme : undefined);
    if (typeof theme?.getLocalizedUrl === 'function') {
      return theme.getLocalizedUrl(url);
    }

    const origin = theme?.routes?.request_origin;
    return (url || '').replace(`${origin}/`, this.prefix);
  }

  getFallbackImageUrl() {
    const url = this.getAttribute('fallback-image-url');
    return url && url.trim().length ? url.trim() : null;
  }

  /**
   * @returns {boolean}
   */
  isAlternativeLayout() {
    return this.getAttribute('data-alternative-layout') === 'true';
  }

  /**
   * @returns {boolean}
   */
  shouldShowCollectionImages() {
    if (!this.isAlternativeLayout()) return true;
    return this.getAttribute('data-show-collection-images') !== 'false';
  }

  /**
   * @returns {string}
   */
  getBackButtonText() {
    return this.getAttribute('data-back-text') || 'Back';
  }

  /**
   * @returns {string}
   */
  getViewAllText() {
    return this.getAttribute('data-view-all-text') || 'View all';
  }

  /**
   * @returns {string}
   */
  getSubmenuChevronSvg() {
    return `
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        aria-hidden="true"
        class="icon"
        style="fill: none;"
      >
        <path xmlns="http://www.w3.org/2000/svg" d="M17 14.6595L22.0888 19.7483C22.1219 19.7813 22.1481 19.8205 22.166 19.8637C22.1839 19.9069 22.1932 19.9532 22.1932 19.9999C22.1932 20.0466 22.1839 20.0929 22.166 20.1361C22.1481 20.1792 22.1219 20.2185 22.0888 20.2515L17 25.3403" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
      </svg>
    `;
  }

  /**
   * @returns {string}
   */
  getBackChevronSvg() {
    return `
      <svg aria-hidden="true" focusable="false" width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>

    `;
  }

  /**
   * @returns {string}
   */
  getCloseButtonHtml() {
    const existingClose = this.querySelector(
      '.drawer-menu-alt__close, .drawer-menu__header-close-button'
    );
    if (existingClose instanceof HTMLElement) {
      return existingClose.outerHTML;
    }

    return `
      <button type="button" class="button close-button menu-drawer__close-button drawer-menu-alt__close drawer-menu__header-close-button" aria-label="close menu drawer">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>
      </button>
    `;
  }

  /**
   * @returns {string | null}
   */
  getLiquidImagePlaceholderHtml() {
    const tpl = this.querySelector('#DrawerMenuFallbackImagePlaceholder');
    if (!(tpl instanceof HTMLTemplateElement)) return null;
    const html = tpl.innerHTML;
    return html && html.trim().length ? html.trim() : null;
  }

  /**
   * @param {string | null | undefined} imageUrl
   * @param {string} alt
   * @returns {string}
   */
  renderMenuItemImage(imageUrl, alt) {
    if (!this.shouldShowCollectionImages()) {
      return '';
    }

    const fallbackUrl = this.getFallbackImageUrl();
    const hasValidImage = typeof imageUrl === 'string' && imageUrl.length > 0;
    const imageAlt = typeof alt === 'string' && alt.length > 0 ? alt : '';
    const finalUrl = hasValidImage ? imageUrl : fallbackUrl;

    if (finalUrl) {
      return `<div class="image"><img src="${finalUrl}" alt="${imageAlt}" class="image__img image--contain" loading="lazy"></div>`;
    }

    return (
      this.getLiquidImagePlaceholderHtml() || "<div class='drawer-menu__image-placeholder'></div>"
    );
  }

  connectedCallback() {
    this.handleLevel3Click = this.handleLevel3Click.bind(this);

    // Prevent multiple initialization
    if (this._drawerMenuInitialized) return;

    const initOnce = () => {
      if (this._drawerMenuInitialized) return;
      this._drawerMenuInitialized = true;
      this.initializeDrawerMenu();
    };

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1]; // Get the latest LCP entry
        if (lastEntry) {
          initOnce();
          observer.disconnect();
        }
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });

      // Fallback using requestAnimationFrame with a maximum number of attempts
      let rafAttempts = 0;
      const maxRafAttempts = 60; // Adjust this number based on your needs

      const rafFallback = () => {
        if (!observer.takeRecords().length && rafAttempts < maxRafAttempts) {
          rafAttempts += 1;
          requestAnimationFrame(rafFallback);
        } else if (rafAttempts >= maxRafAttempts) {
          initOnce();
        }
      };

      // Use requestAnimationFrame for a more performant fallback
      requestAnimationFrame(rafFallback);

      this._domContentLoadedHandler = () => {
        if (!this._drawerMenuInitialized) {
          initOnce();
        }
      };

      // Additional fallback for DOMContentLoaded
      document.addEventListener('DOMContentLoaded', this._domContentLoadedHandler);
    } catch (error) {
      initOnce();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleLevel3Click);
    if (this._domContentLoadedHandler) {
      document.removeEventListener('DOMContentLoaded', this._domContentLoadedHandler);
      this._domContentLoadedHandler = null;
    }
  }

  initializeDrawerMenu() {
    // Initialize drawer menu elements
    this.drawer = this.querySelector('.drawer-menu');
    this.drawerPanel = this.querySelector('.drawer-menu-main');
    this.drawerContainer = this.querySelector('.menu-drawer-container');
    this.templateLevel1 = this.querySelector('#DrawerMenuLevel1');
    this.templateLevel2 = this.querySelector('#DrawerMenuLevel2');

    this.openDrawer();
    this.closeDrawer();

    // Fallback to open drawer if 'open' attribute is present on drawer container (e.g. from SSR or if JavaScript loads after an interaction)
    if (this.drawerContainer?.hasAttribute('open')) {
      this.handleOpen();
    }

    this.injectMainMenu();

    if (this.isAlternativeLayout()) {
      this.initializeAlternativeDrawerMenu();
    } else {
      const rootNavItems = this.querySelectorAll('.drawer-menu-nav-items__item');
      this.addClickListeners(rootNavItems, 1);

      // Level 3 mobile - using event delegation
      this.addEventListener('click', this.handleLevel3Click);
    }
  }

  initializeAlternativeDrawerMenu() {
    const rootPanel = this.querySelector('.drawer-menu-alt__panel--root');
    const menuData = DrawerMenu.getMenuData();

    if (rootPanel && menuData?.menuItems) {
      const rootItems = rootPanel.querySelectorAll('.drawer-menu-nav-items__item');
      this.addAlternativeClickListeners(rootItems, menuData.menuItems);
    }
  }

  /**
   * @param {HTMLElement[]} navItems
   * @param {Array<{ title: string, url: string, image?: string, subMenu?: Array<unknown> }>} menuItems
   */
  addAlternativeClickListeners(navItems, menuItems) {
    navItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        const target = e.target instanceof Element ? e.target : e.target?.parentElement;
        if (target?.closest('a.drawer-menu-alt__parent')) return;

        const menuItem = menuItems[index];
        if (!menuItem) {
          const link = item.querySelector('a');
          if (link?.href) {
            window.location = link.href;
          }
          return;
        }

        const hasSubMenu = menuItem.subMenu && menuItem.subMenu.length > 0;

        if (hasSubMenu) {
          e.preventDefault();
          this.pushAlternativePanel(menuItem);
        } else {
          const link = item.querySelector('a.drawer-menu-item');
          if (link?.href) {
            window.location = link.href;
          }
        }
      });
    });
  }

  /**
   * @param {{ title: string, url: string, image?: string, subMenu?: Array<unknown> }} menuData
   */
  pushAlternativePanel(menuData) {
    const track = this.querySelector('[data-alt-track]');
    if (!track || !menuData?.subMenu) return;

    const panel = this.createAlternativePanel(menuData);
    track.appendChild(panel);

    const depth = track.querySelectorAll('.drawer-menu-alt__panel').length - 1;
    track.style.transform = `translateX(-${depth * 100}%)`;

    const navItems = panel.querySelectorAll('.drawer-menu-nav-items__item');
    this.addAlternativeClickListeners(navItems, menuData.subMenu);

    panel.querySelector('.drawer-menu-alt__back')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.popAlternativePanel();
    });

    panel.scrollTop = 0;
  }

  /**
   * @param {{ title: string, url: string, image?: string, subMenu?: Array<unknown> }} menuData
   * @returns {HTMLDivElement}
   */
  createAlternativePanel(menuData) {
    const panel = document.createElement('div');
    panel.className = 'drawer-menu-alt__panel';

    const listItemsHtml = menuData.subMenu
      .map((subItem) => this.renderAlternativeMenuItemHtml(subItem))
      .join('');

    panel.innerHTML = `
      <div class="drawer-menu-alt__toolbar">
        <button type="button" class="drawer-menu-alt__back" aria-label="${this.getBackButtonText()}">
          ${this.getBackChevronSvg()}
          <span class="drawer-menu-alt__back-text">${this.getBackButtonText()}</span>
        </button>
        ${this.getCloseButtonHtml()}
      </div>
      <a href="${DrawerMenu.getUrl(menuData.url)}" class="drawer-menu-alt__parent">
        <p class="drawer-menu-alt__parent-title">${menuData.title}</p>
        <p class="drawer-menu-alt__parent-subtitle">${this.getViewAllText()}</p>
      </a>
      <hr class="drawer-menu-alt__divider">
      <ul class="drawer-menu-nav-items drawer-menu-alt__list">
        ${listItemsHtml}
      </ul>
    `;

    return panel;
  }

  /**
   * @param {{ title: string, url: string, image?: string, subMenu?: Array<unknown> }} menuItem
   * @returns {string}
   */
  renderAlternativeMenuItemHtml(menuItem) {
    const hasSubMenu = menuItem.subMenu && menuItem.subMenu.length > 0;

    return `
      <li class="drawer-menu-nav-items__item${hasSubMenu ? ' has-submenu' : ''}">
        <a href="${DrawerMenu.getUrl(menuItem.url)}" class="drawer-menu-item">
          ${this.renderMenuItemImage(menuItem.image, menuItem.title)}
          <p class="text" style="--text-color: var(--color--secondary);">
            ${menuItem.title}
          </p>
          ${hasSubMenu ? this.getSubmenuChevronSvg() : ''}
        </a>
      </li>
    `;
  }

  popAlternativePanel() {
    const track = this.querySelector('[data-alt-track]');
    if (!track) return;

    const panels = track.querySelectorAll('.drawer-menu-alt__panel');
    if (panels.length <= 1) return;

    const lastPanel = panels[panels.length - 1];
    const depth = panels.length - 2;
    track.style.transform = `translateX(-${depth * 100}%)`;

    const onEnd = (event) => {
      if (event.target !== track) return;
      track.removeEventListener('transitionend', onEnd);
      lastPanel.remove();
    };

    track.addEventListener('transitionend', onEnd);
  }

  resetAlternativePanels() {
    const track = this.querySelector('[data-alt-track]');
    if (!track) return;

    track
      .querySelectorAll('.drawer-menu-alt__panel:not(.drawer-menu-alt__panel--root)')
      .forEach((panel) => {
        panel.remove();
      });
    track.style.transform = 'translateX(0)';
  }

  openDrawer() {
    const headerDrawerButtons = document.querySelectorAll('.header__drawer .header__icon--menu');
    headerDrawerButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.handleOpen();
      });
    });
  }

  closeDrawer() {
    const closeSelector = this.isAlternativeLayout()
      ? '.drawer-menu__overlay'
      : '.drawer-menu__overlay, .drawer-menu__header-close-button';

    this.querySelectorAll(closeSelector).forEach((button) => {
      button.addEventListener('click', () => {
        this.handleClose();
      });
    });

    if (this.isAlternativeLayout()) {
      this.drawer?.addEventListener('click', (e) => {
        const target = e.target instanceof Element ? e.target : e.target?.parentElement;
        if (target?.closest('.drawer-menu-alt__close')) {
          this.handleClose();
        }
      });
    }
  }

  // @ts-ignore
  addClickListeners(navItems, level, parentIndex = null) {
    // @ts-ignore
    navItems.forEach((item, index) => {
      // @ts-ignore
      item.addEventListener('click', (e) => {
        const footerLink = e.target.closest('.drawer-menu-navigation-footer__link');
        if (footerLink) {
          const link = footerLink.href;
          window.location = link;
        }
        if (
          e.target.classList.contains('drawer-menu-cards-navigation__item') ||
          e.target.closest('.drawer-menu-cards-navigation__item')
        ) {
          try {
            const container = e.target.closest('.drawer-menu-cards-navigation__item');
            if (!container) {
              throw new Error('Could not find drawer menu cards navigation item element');
            }
            const anchor = container.querySelector('a');
            if (!anchor || !anchor.href) {
              throw new Error('Drawer menu cards navigation item is missing href');
            }

            const link = anchor.href;
            if (!link) {
              throw new Error('Drawer menu cards navigation item is missing href');
            }

            window.location = link;
          } catch (error) {
            console.error('Error navigating drawer menu cards:', error);
          }
        }
        if (!item.classList.contains('has-submenu')) {
          window.location = item.querySelector('a').href;
        } else if (!item.querySelector(`.drawer-menu-level-${level}`)) {
          e.preventDefault();
          if (level === 1) {
            this.injectSubmenuLevel1(item, index);
          } else if (level === 2) {
            this.injectSubmenuLevel2(item, parentIndex, index);
          }
        } else if (item.querySelector(`.drawer-menu-level-${level}`)) {
          e.preventDefault();
          const drawerMenu = item.querySelector(`.drawer-menu-level-${level}`);
          this.toggleSubmenuOpenState(drawerMenu, true);
        }
      });
    });
  }

  injectMainMenu() {
    const placeholder = this.querySelector('.main-menu__placeholder');
    const menuData = DrawerMenu.getMenuData();

    if (!menuData || !menuData.menuItems || !placeholder) {
      return;
    }

    // @ts-ignore
    const items = menuData.menuItems.map((menuItem) => {
      const listItem = document.createElement('li');
      const hasSubMenu = menuItem.subMenu && menuItem.subMenu.length > 0;
      listItem.className = `drawer-menu-nav-items__item${hasSubMenu ? ' has-submenu' : ''}`;
      listItem.innerHTML = `
          <a href="${DrawerMenu.getUrl(menuItem.url)}" class="drawer-menu-item">
            ${this.renderMenuItemImage(menuItem.image, menuItem.title)}
            <p class="text" style="--text-color: var(--color--secondary);">
              ${menuItem.title}
            </p>
            ${
              hasSubMenu
                ? `
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                aria-hidden="false"
                aria-label="open menu"
                class="icon "
                style="fill: none;"
              >
                <path xmlns="http://www.w3.org/2000/svg" d="M17 14.6595L22.0888 19.7483C22.1219 19.7813 22.1481 19.8205 22.166 19.8637C22.1839 19.9069 22.1932 19.9532 22.1932 19.9999C22.1932 20.0466 22.1839 20.0929 22.166 20.1361C22.1481 20.1792 22.1219 20.2185 22.0888 20.2515L17 25.3403"  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
              </svg>
            `
                : ''
            }
          </a>
        `;
      return listItem;
    });

    placeholder.replaceWith(...items);
  }

  // @ts-ignore
  injectSubmenuLevel1(item, index) {
    // Fetch data dynamically for the hovered item
    const menuData = DrawerMenu.getMenuData().menuItems[index];
    if (!menuData) return;

    // Clone the template
    // @ts-ignore
    const templateContent = this.templateLevel1.content.cloneNode(true);

    // Update template content
    const titleElement = templateContent.querySelector('.drawer-menu-navigation-header h3');
    const navList = templateContent.querySelector('.drawer-menu-nav-items');
    const footerLink = templateContent.querySelector('.drawer-menu-navigation-footer__link');
    const backButton = templateContent.querySelector('.drawer-menu-navigation-header__back-button');
    const closeButton = templateContent.querySelector('.drawer-menu__header-close-button');
    const submenu = templateContent.querySelector('.drawer-menu-level-1');
    this.toggleSubmenuOpenState(submenu, true);

    // @ts-ignore
    backButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleSubmenuOpenState(submenu, false);
    });

    closeButton.addEventListener('click', () => {
      this.handleClose();
    });

    // Update title and sub-menu items
    titleElement.textContent = menuData.title;
    navList.innerHTML = ''; // Clear existing items

    // Add sub-menu items
    // @ts-ignore
    menuData.subMenu.forEach((subItem) => {
      const listItem = document.createElement('li');
      const hasSubMenu = subItem.subMenu && subItem.subMenu.length > 0;
      listItem.className = `drawer-menu-nav-items__item${hasSubMenu ? ' has-submenu' : ''}`;
      listItem.innerHTML = `
          <a href="${DrawerMenu.getUrl(subItem.url)}" class="drawer-menu-item">
            ${this.renderMenuItemImage(subItem.image, subItem.title)}
            <p class="text" style="--text-color: var(--color--secondary);">
              ${subItem.title}
            </p>
            ${
              hasSubMenu
                ? `
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                aria-hidden="false"
                aria-label="open menu"
                class="icon "
                style="fill: none;"
              >
                <path xmlns="http://www.w3.org/2000/svg" d="M17 14.6595L22.0888 19.7483C22.1219 19.7813 22.1481 19.8205 22.166 19.8637C22.1839 19.9069 22.1932 19.9532 22.1932 19.9999C22.1932 20.0466 22.1839 20.0929 22.166 20.1361C22.1481 20.1792 22.1219 20.2185 22.0888 20.2515L17 25.3403"  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/>
              </svg>
            `
                : ''
            }
          </a>
        `;
      navList.appendChild(listItem);
    });

    // Update footer link
    footerLink.href = DrawerMenu.getUrl(menuData.url);
    const seeMenuText = this.getAttribute('see-menu-text')?.replace('[menu]', menuData.title);
    footerLink.querySelector('span').textContent = `${seeMenuText}`;

    // Inject the updated template into the hovered item
    item.appendChild(templateContent);

    // Trigger Level 2 submenus after Level 1 is injected
    this.addClickListeners(item.querySelectorAll('.drawer-menu-nav-items__item'), 2, index);
  }

  // @ts-ignore
  injectSubmenuLevel2(item, level1Index, level2Index) {
    // Find the corresponding Level 2 menu data based on Level 1 item index and Level 2 submenu index
    const menuData = DrawerMenu.getMenuData().menuItems[level1Index].subMenu[level2Index];
    if (!menuData) return;

    // @ts-ignore
    const templateContent = this.templateLevel2.content.cloneNode(true);
    const titleElement = templateContent.querySelector('.drawer-menu-navigation-header h3');
    const navList = templateContent.querySelector('.drawer-menu-cards-navigation__list');
    const backButton = templateContent.querySelector('.drawer-menu-navigation-header__back-button');
    const footerLink = templateContent.querySelector('.drawer-menu-navigation-footer__link');
    const closeButton = templateContent.querySelector('.drawer-menu__header-close-button');
    const submenu = templateContent.querySelector('.drawer-menu-level-2');
    this.toggleSubmenuOpenState(submenu, true);

    closeButton.addEventListener('click', () => {
      this.handleClose();
    });

    // @ts-ignore
    backButton.addEventListener('click', (e) => {
      e.stopPropagation();
      const menuLevel2 = item.querySelector('.drawer-menu-level-2');
      this.toggleSubmenuOpenState(menuLevel2, false);
    });

    titleElement.textContent = menuData.title;
    navList.innerHTML = '';

    // @ts-ignore
    menuData.subMenu.forEach((subItem) => {
      const listItem = document.createElement('li');
      listItem.className = 'drawer-menu-cards-navigation__item';
      listItem.innerHTML = `
          <a href="${DrawerMenu.getUrl(subItem.url)}" class="drawer-menu-card-navigation">
            ${this.renderMenuItemImage(subItem.image, subItem.title)}
            <p class="text" style="--text-color: var(--color--secondary);">${subItem.title}</p>
          </a>
        `;
      navList.appendChild(listItem);
    });
    // Update footer link
    footerLink.href = DrawerMenu.getUrl(menuData.url);
    const seeMenuText = this.getAttribute('see-menu-text')?.replace('[menu]', menuData.title);
    footerLink.querySelector('span').textContent = `${seeMenuText}`;

    item.appendChild(templateContent);
  }

  handleOpen() {
    this.drawer?.classList.add('drawer-menu--show');
    document.body.classList.add('overflow-hidden');
  }

  handleClose() {
    this.drawer?.classList.remove('drawer-menu--show');
    document.body.classList.remove('overflow-hidden');

    if (this.isAlternativeLayout()) {
      this.resetAlternativePanels();
    }

    // Wait for the drawer panel's transition to end before removing the 'open' attribute to allow the closing animation to play
    // @ts-ignore
    const onEnd = (event) => {
      if (!this.drawerPanel || event.target !== this.drawerPanel) return;

      this.drawerPanel.removeEventListener('transitionend', onEnd);

      // cleanup AFTER animation finishes
      this.drawerContainer?.removeAttribute('open');
    };

    this.drawerPanel?.addEventListener('transitionend', onEnd);
  }

  // @ts-ignore
  handleLevel3Click(e) {
    const anchor = e.target.closest('.drawer-menu-card-navigation');

    if (!anchor) return;

    // @ts-ignore
    if (!window.dataLayer) window.dataLayer = [];

    // @ts-ignore
    window.dataLayer.push({
      event: 'select_navitem_dev',
      nav_level: '3',
      nav_type: 'main-navigation',
      category_name: anchor.textContent?.trim(),
      category_id: anchor.textContent?.trim(),
    });
  }

  /**
   * Sets the submenu open state class on the drawer.
   * @param {HTMLElement} submenu
   * @param {boolean} isOpen
   */
  toggleSubmenuOpenState(submenu, isOpen) {
    if (!submenu) return;

    if (!isOpen) {
      submenu.classList.remove('submenu--open');
      return;
    }

    submenu.classList.add('submenu--open');

    // Level 1 submenu reset scroll
    if (submenu.classList.contains('drawer-menu-level-1') && this.drawerPanel) {
      this.drawerPanel.scrollTop = 0;
    }

    // Level 2 submenu reset scroll
    if (submenu.classList.contains('drawer-menu-level-2')) {
      // Defer scrolling to the next frame to ensure DOM insertion
      requestAnimationFrame(() => {
        const parentLevel1 = submenu.closest('.drawer-menu-level-1');
        if (parentLevel1) parentLevel1.scrollTop = 0;
      });
    }
  }
}

if (!customElements.get('drawer-menu')) {
  customElements.define('drawer-menu', DrawerMenu);
}
